import React from 'react';
import { AlertCircleIcon, Loader2Icon, PlusIcon } from 'lucide-react';
import { Button } from '../../ui/Button';
import { SelectField, TextField } from '../../ui/Field';
import { caseEntryTypes, caseEntryUnits } from '../../../types/caseEntry';
import type { CaseEntryType, CaseEntryUnit } from '../../../types/caseEntry';
import type { useCaseEntryForm } from './useCaseEntryForm';
const placeholders: Record<CaseEntryType, string> = {
  Timer: 'Fx belægningsarbejde',
  Materialer: 'Fx kantskinne',
  Kørsel: 'Fx kørsel til og fra opgaven',
  'Servicevogn og værktøj': 'Fx servicevogn og værktøj',
  Maskiner: 'Fx pladevibrator eller ukrudtsbrænder',
  Bortskaffelse: 'Fx jord eller affald til genbrugsplads',
  Andet: 'Beskriv udgiften'
};
const priceLabel: Record<CaseEntryType, string> = {
  Timer: 'Timepris (kr.)',
  Materialer: 'Salgspris (kr.)',
  Kørsel: 'Pris pr. enhed (kr.)',
  'Servicevogn og værktøj': 'Beløb (kr.)',
  Maskiner: 'Pris (kr.)',
  Bortskaffelse: 'Pris (kr.)',
  Andet: 'Pris (kr.)'
};
interface EntryFormProps {
  api: ReturnType<typeof useCaseEntryForm>;
  saving: boolean;
  saveError: string;
  onSubmit: (event: React.FormEvent) => void;
}
export const EntryForm = React.forwardRef<HTMLFormElement, EntryFormProps>(function EntryForm({
  api,
  saving,
  saveError,
  onSubmit
}, ref) {
  const {
    form,
    setField,
    chooseType,
    reset,
    editingId,
    formError
  } = api;
  const isFixedAmount = form.type === 'Servicevogn og værktøj';
  const isHours = form.type === 'Timer';
  return <form ref={ref} onSubmit={onSubmit} className="scroll-mt-28 rounded-xl border border-line bg-canvas p-4 md:p-5" noValidate data-id="element-189" data-mp="i6dbpw8sz9r">
      <p className="text-sm font-semibold text-ink" data-id="element-190" data-mp="3rp94ihc2z5jz">{editingId ? 'Ret linje' : 'Registrér forbrug'}</p>
      <div className="mt-3 flex flex-wrap gap-1.5" role="group" aria-label="Type" data-id="element-191" data-mp="3nop9pkezce10">
        {caseEntryTypes.map(type => <button key={type} type="button" disabled={Boolean(editingId) && form.type !== type} onClick={() => chooseType(type)} aria-pressed={form.type === type} className={`whitespace-nowrap rounded-full border px-3 py-1.5 text-xs transition-colors duration-150 disabled:opacity-40 ${form.type === type ? 'border-accent/50 bg-accent/15 text-ink' : 'border-line bg-white text-ink-soft hover:border-ink/30'}`} data-id="element-192" data-mp="3ipvv1onyigz9">
            {type}
          </button>)}
      </div>

      <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-6" data-id="element-193" data-mp="3rqx3pk8awpbs">
        <TextField id="forbrug-dato" label="Dato" type="date" className="lg:col-span-2" value={form.date} onChange={event => setField('date', event.target.value)} data-id="element-194" data-mp="3rq5d6ntk9hsd" />
        <TextField id="forbrug-beskrivelse" label="Beskrivelse" className="sm:col-span-2 lg:col-span-4" placeholder={placeholders[form.type]} value={form.description} onChange={event => setField('description', event.target.value)} data-id="element-195" data-mp="9lk3xymchhzc" />

        {!isFixedAmount && <TextField id="forbrug-antal" label={isHours ? 'Timer' : form.type === 'Kørsel' ? 'Km / antal' : 'Antal'} inputMode="decimal" placeholder={isHours ? 'Fx 3,5' : '1'} className="lg:col-span-2" value={form.quantity} onChange={event => setField('quantity', event.target.value)} data-id="element-196" data-mp="1u2jkhuq4s6bi" />}
        {!isHours && !isFixedAmount && <SelectField id="forbrug-enhed" label="Enhed" className="lg:col-span-2" value={form.unit} onChange={event => setField('unit', event.target.value as CaseEntryUnit)} options={caseEntryUnits.map(unit => ({
        value: unit,
        label: unit
      }))} data-id="element-197" data-mp="1b6l60gpzm6my" />}
        {form.type === 'Materialer' && <TextField id="forbrug-indkob" label="Indkøbspris (kr.)" inputMode="decimal" placeholder="Valgfri" className="lg:col-span-2" value={form.purchasePrice} onChange={event => setField('purchasePrice', event.target.value)} data-id="element-198" data-mp="2whjtfrdzr4zf" />}
        <TextField id="forbrug-pris" label={priceLabel[form.type]} inputMode="decimal" placeholder="0" className="lg:col-span-2" value={form.unitPrice} onChange={event => setField('unitPrice', event.target.value)} data-id="element-199" data-mp="9mdeiil527t3" />
        <SelectField id="forbrug-moms" label="Moms" className="lg:col-span-2" value={form.vatRate} onChange={event => setField('vatRate', event.target.value)} options={[{
        value: '25',
        label: '25 %'
      }, {
        value: '0',
        label: '0 %'
      }]} data-id="element-200" data-mp="ndrwqn7824z3" />
      </div>

      {(formError || saveError) && <p role="alert" className="mt-4 flex items-start gap-2 text-sm text-danger" data-id="element-201" data-mp="128oaffxsvkjk">
          <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-202" data-mp="2mr07v7jyeihc" />
          {formError || saveError}
        </p>}

      <div className="mt-4 flex flex-wrap gap-2" data-id="element-203" data-mp="2cudokn79zzhf">
        <Button type="submit" size="sm" disabled={saving} data-id="element-204" data-mp="2cv5kjz4sbz88">
          {saving ? <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-205" data-mp="27x40bavmhtv6" /> : !editingId && <PlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-206" data-mp="35j1ckqjet2yo" />}
          {editingId ? 'Gem ændring' : 'Tilføj linje'}
        </Button>
        {editingId && <button type="button" onClick={reset} className="inline-flex h-9 items-center rounded-full border border-line px-3.5 text-sm text-ink-soft transition-colors duration-150 hover:border-ink/30 hover:text-ink" data-id="element-207" data-mp="2vspzaddu5hf4">
            Annuller
          </button>}
      </div>
    </form>;
});